import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { useOperatorProfileStore } from '../stateManagers/stores/operatorProfileStore';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DashboardData {
  scope: { institute_id: string; branch_id: string };
  batches: { total: number; active: number; inactive: number };
  teachers: { total: number; active: number; registered: number };
  students: { total: number; active: number; enrolled: number; registered: number };
  courses: { total: number; active: number };
  recent_enrollments: {
    enrollment_id: string;
    enrollment_no: string;
    student_id: string;
    student_name: string;
    course_id: string;
    course_name: string;
    batch_id: string;
    batch_name: string;
    date_of_admission: string;
    enrolled_at: string;
  }[];
}

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  title: string;
  total: number;
  icon: string;
  accent?: boolean;
  badges?: { label: string; value: number; color: string }[];
}> = ({ title, total, icon, accent = false, badges = [] }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: accent
          ? 'linear-gradient(145deg, #0e225d 0%, #1a3370 60%, #0a1a3d 100%)'
          : 'white',
        borderRadius: '20px',
        padding: '24px',
        border: accent ? 'none' : '1px solid #E5E7EB',
        boxShadow: hovered
          ? accent
            ? '0 16px 40px rgba(14,34,93,0.35)'
            : '0 12px 32px rgba(14,34,93,0.14)'
          : accent
          ? '0 4px 16px rgba(14,34,93,0.25)'
          : '0 2px 8px rgba(14,34,93,0.06)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Decorative orb */}
      <div style={{
        position: 'absolute', top: '-40px', right: '-40px',
        width: '130px', height: '130px',
        background: accent ? 'rgba(251,178,31,0.1)' : 'rgba(14,34,93,0.04)',
        borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Icon */}
        <div style={{
          width: '44px', height: '44px',
          background: accent ? 'rgba(251,178,31,0.18)' : 'rgba(14,34,93,0.07)',
          border: accent ? '1px solid rgba(251,178,31,0.35)' : '1px solid rgba(14,34,93,0.1)',
          borderRadius: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '16px',
        }}>
          <i className={icon} style={{
            fontSize: '1.1rem',
            color: accent ? '#fbb21f' : '#0e225d',
          }} />
        </div>

        <p style={{
          margin: '0 0 4px 0',
          fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: accent ? 'rgba(255,255,255,0.55)' : '#9ca3af',
        }}>
          {title}
        </p>

        <p style={{
          margin: '0 0 16px 0',
          fontSize: '2.4rem', fontWeight: '800', lineHeight: 1,
          color: accent ? 'white' : '#0e225d',
          letterSpacing: '-1px',
        }}>
          {total}
        </p>

        {/* Badges row */}
        {badges.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {badges.map((b, i) => (
              <span key={i} style={{
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.4px',
                background: b.color === 'gold'
                  ? (accent ? '#fbb21f' : 'rgba(251,178,31,0.14)')
                  : b.color === 'green'
                  ? 'rgba(22,163,74,0.1)'
                  : 'rgba(239,68,68,0.1)',
                color: b.color === 'gold'
                  ? (accent ? '#0e225d' : '#b8860b')
                  : b.color === 'green'
                  ? '#15803d'
                  : '#dc2626',
                border: b.color === 'gold'
                  ? (accent ? 'none' : '1px solid rgba(251,178,31,0.3)')
                  : b.color === 'green'
                  ? '1px solid rgba(22,163,74,0.2)'
                  : '1px solid rgba(239,68,68,0.2)',
              }}>
                {b.value} {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  // Inject menu / scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .enrollment-row:hover { background: rgba(14,34,93,0.03) !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) {
      fetchDashboard();
    }
  }, [profile]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ success: boolean; data: DashboardData }>('/v1/admin/dashboard');
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to load dashboard data.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  // ── Guard renders ─────────────────────────────────────────────────────────

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading profile...</p>
      </div>
    );
  }

  if (!profile?.institute_id || !profile?.branch_id) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="warn" text="Institute or Branch information is missing. Please contact support." style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error} style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  if (!data) return null;

  const { batches, teachers, students, courses, recent_enrollments } = data;

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* ── Stat Cards Grid ──────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <StatCard
          title="Total Batches"
          total={batches.total}
          icon="pi pi-th-large"
          accent
          badges={[
            { label: 'Active', value: batches.active, color: 'gold' },
            { label: 'Inactive', value: batches.inactive, color: 'red' },
          ]}
        />
        <StatCard
          title="Total Teachers"
          total={teachers.total}
          icon="pi pi-users"
          badges={[
            { label: 'Active', value: teachers.active, color: 'green' },
            { label: 'Registered', value: teachers.registered, color: 'gold' },
          ]}
        />
        <StatCard
          title="Total Students"
          total={students.total}
          icon="pi pi-user"
          badges={[
            { label: 'Enrolled', value: students.enrolled, color: 'green' },
            { label: 'Registered', value: students.registered, color: 'gold' },
          ]}
        />

      </div>

      {/* ── Quick Stats Bar ──────────────────────────────────────────────── */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '20px 24px',
        marginBottom: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
        border: '1px solid #E5E7EB',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0',
      }}>
        {[
          { label: 'Active Batches', value: batches.active, icon: 'pi pi-check-circle', color: '#15803d', bg: 'rgba(22,163,74,0.08)' },
          { label: 'Active Teachers', value: teachers.active, icon: 'pi pi-user-edit', color: '#0e225d', bg: 'rgba(14,34,93,0.07)' },
          { label: 'Active Students', value: students.active, icon: 'pi pi-user-plus', color: '#b8860b', bg: 'rgba(251,178,31,0.12)' },

        ].map((item, idx, arr) => (
          <div key={idx} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '12px 20px',
            borderRight: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
          }}>
            <div style={{
              width: '38px', height: '38px', flexShrink: 0,
              background: item.bg, borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className={item.icon} style={{ color: item.color, fontSize: '0.9rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#0e225d', lineHeight: 1, letterSpacing: '-0.5px' }}>
                {item.value}
              </p>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.72rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.3px' }}>
                {item.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Recent Enrollments ───────────────────────────────────────────── */}
      <div style={{
        background: 'white', borderRadius: '20px',
        boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}>
        {/* Table Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '4px', height: '18px',
              background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px',
            }} />
            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0e225d' }}>
              Recent Enrollments
            </span>
          </div>
          <span style={{
            padding: '4px 12px',
            background: 'rgba(14,34,93,0.06)', borderRadius: '20px',
            fontSize: '0.72rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.4px',
          }}>
            {recent_enrollments.length} Latest
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Enrollment No', 'Student', 'Course', 'Batch', 'Date of Admission'].map((h) => (
                  <th key={h} style={{
                    padding: '12px 20px', textAlign: 'left',
                    fontSize: '0.68rem', fontWeight: '700', color: '#9ca3af',
                    letterSpacing: '0.6px', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_enrollments.map((enr, idx) => (
                <tr
                  key={enr.enrollment_id}
                  className="enrollment-row"
                  style={{
                    borderTop: '1px solid #f3f4f6',
                    transition: 'background 0.15s ease',
                    background: 'white',
                  }}
                >
                  {/* Enrollment No */}
                  <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: 'rgba(14,34,93,0.06)',
                      borderRadius: '8px',
                      fontSize: '0.72rem', fontWeight: '700', color: '#0e225d',
                      letterSpacing: '0.3px',
                    }}>
                      {enr.enrollment_no}
                    </span>
                  </td>

                  {/* Student */}
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '34px', height: '34px', flexShrink: 0,
                        background: `hsl(${(idx * 67) % 360}, 55%, 88%)`,
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: '800',
                        color: `hsl(${(idx * 67) % 360}, 55%, 35%)`,
                      }}>
                        {enr.student_name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: '0.87rem', fontWeight: '600', color: '#1f2937', whiteSpace: 'nowrap' }}>
                        {enr.student_name}
                      </span>
                    </div>
                  </td>

                  {/* Course */}
                  <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: 'rgba(251,178,31,0.12)',
                      border: '1px solid rgba(251,178,31,0.25)',
                      borderRadius: '8px',
                      fontSize: '0.75rem', fontWeight: '700', color: '#b8860b',
                    }}>
                      {enr.course_name}
                    </span>
                  </td>

                  {/* Batch */}
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{ fontSize: '0.84rem', fontWeight: '600', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {enr.batch_name}
                    </span>
                  </td>

                  {/* Date */}
                  <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="pi pi-calendar" style={{ color: '#0e225d', fontSize: '0.8rem', opacity: 0.5 }} />
                      <span style={{ fontSize: '0.83rem', fontWeight: '600', color: '#374151' }}>
                        {enr.date_of_admission}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {recent_enrollments.length === 0 && (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <i className="pi pi-inbox" style={{ fontSize: '2rem', color: '#0e225d', opacity: 0.2 }} />
              <p style={{ marginTop: '12px', color: '#9ca3af', fontSize: '0.9rem' }}>No recent enrollments</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;